/**
 * Utils.js
 * 
 * objectToUrlString ahora omite los parámetros de tipo Array 
 * y Object, además se agregaron excepciones para: 
 *  value === undefined || value === 'undefined'
 * 
 * @version 4.1
 * @author Daniel Lepe
 * @date March 18th, 2025.
 * 
 * Integración de getDecimalFromFraction. 
 * Para conversión de strings fraccionarios a decimales.
 * 
 * @version 4.0
 * @author Daniel Lepe
 * @date Feb 10th, 2025.
 * 
 * Actualización con versión de StoicFiles.
 * 
 * @version 3.0
 * @author Daniel Lepe
 * @date January 25th, 2025.
 * 
 * Some handy functions
 * Added function toHumanReadable; it turns camelCase and 
 * snake_case
 * into "Human Case";
 * 
 * @version 2.1
 * @author Daniel Lepe
 * @date January 24th, 2025.
 * 
 * Added quick function arrayToObject, swalMSG and mixedMSG
 * 
 * @version 2.0
 * @author Daniel Lepe
 * @date June 30th, 2023
 */
import Vue from 'vue';
import swal from 'sweetalert'
import swal2 from 'sweetalert2'
import _ from 'lodash'
import axios from 'axios'
import $router from './router';


import { translation as transEn } from './langs/en.js'
import { translation as transFr } from './langs/fr.js'
import { translation as transEs } from './langs/es.js'
import { mainServer as apiURIMain } from '@/apiConfig';

const singular = (text) => {
    try {
        if (typeof text !== 'string')
            throw 'text is not a string';
        // Basic sanitation
        // Remove trailing spaces
        text = text.trim();
        const lowerText = text.toLowerCase();

        // Handle "ies" ending, but avoid exceptions like "Excercises"
        if (lowerText.endsWith('ies')) {
            // Exception list for words ending in "ies" that should not be converted
            // and their singular forms
            const iesExceptions = {
                'series': 'series',
                'species': 'species',
                'movies': 'movie',
                'bodies': 'body',
                'parties': 'party',
                'stories': 'story',
                'cities': 'city',
                'countries': 'country',
                'ladies': 'lady',
                'families': 'family',
                'batteries': 'battery',
                'libraries': 'library',
                'categories': 'category',
                'technologies': 'technology',
                'strategies': 'strategy',
                'faculties': 'faculty',
                'activities': 'activity',
                'abilities': 'ability',
                'possibilities': 'possibility',
                'responsibilities': 'responsibility',
                'opportunities': 'opportunity',
                'communities': 'community',
                'universities': 'university',
                'authorities': 'authority',
                'properties': 'property',
                'priorities': 'priority',
                'varieties': 'variety',
                'identities': 'identity',
                'utilities': 'utility',
                'qualities': 'quality',
                'quantities': 'quantity',
                'enemies': 'enemy',
                'remedies': 'remedy',
                'policies': 'policy',
                'currencies': 'currency',
                'companies': 'company'
            };
            if (iesExceptions[lowerText]) {
                // Return the singular form for the exception
                // Preserve original casing for the first letter
                const singularException = iesExceptions[lowerText];
                if (text[0] === text[0].toUpperCase()) {
                    return singularException.charAt(0).toUpperCase() + singularException.slice(1);
                }
                return singularException;
            } else {
                // e.g. "theses" -> "thesis"
                return text.replace(/ses$/i, 'sis');
            }
        }
        if (lowerText.endsWith('ves')) {
            // Exception list for words ending in "ves" that should not be converted
            // and their singular forms
            const vesExceptions = {
                'wives': 'wife',
                'knives': 'knife',
                'lives': 'life',
            };
            if (vesExceptions[lowerText]) {
                // Return the singular form for the exception
                // Preserve original casing for the first letter
                const singularException = vesExceptions[lowerText];
                if (text[0] === text[0].toUpperCase()) {
                    return singularException.charAt(0).toUpperCase() + singularException.slice(1);
                }
                return singularException;
            } else {
                // e.g. "wolves" -> "wolf"
                return text.replace(/ves$/i, 'f');
            }
        }
        if (lowerText.endsWith('ses')) {
            // Exception list for words ending in "ses  " that should not be converted
            // and their singular forms
            const sesExceptions = {
                'excercises': 'excercise',
                'theses': 'thesis',
                'crises': 'crisis',
                'analyses': 'analysis',
                'diagnoses': 'diagnosis',
                'parentheses': 'parenthesis',
                'syntheses': 'synthesis',
                'hypotheses': 'hypothesis',
                'oases': 'oasis',
                'axes': 'axis',
                'testes': 'testis',
                'bases': 'basis',
            };
            if (sesExceptions[lowerText]) {
                // Return the singular form for the exception
                // Preserve original casing for the first letter
                const singularException = sesExceptions[lowerText];
                if (text[0] === text[0].toUpperCase()) {
                    return singularException.charAt(0).toUpperCase() + singularException.slice(1);
                }
                return singularException;
            } else {
                // e.g. "companies" -> "company"
                return text.replace(/ses$/i, 's');
            }
        }
        if (lowerText.endsWith('xes')) {
            // e.g. "boxes" -> "box"
            return text.replace(/es$/i, '');
        }
        if (lowerText.endsWith('zes')) {
            // e.g. "quizzes" -> "quiz"
            return text.replace(/zes$/i, 'z');
        }
        if (lowerText.endsWith('s') && !lowerText.endsWith('ss')) {
            // e.g. "cars" -> "car", but not "glass" -> "glas"
            return text.replace(/s$/i, '');
        }
        return text;
    } catch (e) {
        console.error(e)
        return text
    }
};

const Toast = swal2.mixin({
        toast: true,
        position: 'bottom-end',
        showConfirmButton: false,
        timer: 8000
    }),
    arrayToObject = (arr, key) => {
        return _.keyBy(arr, key);
    },
    toHumanReadable = (str) => {
        // Handle snake_case
        if (str.includes('_')) {
            return str
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        }

        // Handle camelCase
        return str
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();
    },
    confirmFirst = async ({ text, title, goLabel, cancelLabel }) => {
        if (!goLabel) {
            goLabel = 'Continuar';
        }
        if (!cancelLabel) {
            cancelLabel = 'Cancelar';
        }
        if (!title) {
            title = '¿Seguro que deseas continuar?';
        }
        let response = await swal({
            icon: 'info',
            title,
            text,
            buttons: {
                cancel: cancelLabel,
                danger: goLabel
            }
        })
        return response
    },
    notify = (e) => {
        console.error(e)
        Toast.fire({
            type: 'warning',
            title: e
        });
    },
    toastMSG = (msg, classTXT) => {
        if (msg) {
            // mobiscroll.snackbar({
            //     display: 'top',
            //     theme: 'ios',
            //     message: msg,
            //     color: (classTXT) ? classTXT : 'warning',
            //     duration: 3000
            // })
            Toast.fire({
                type: (classTXT) ? classTXT : 'warning',
                title: msg
            });
        }
    },
    swalMSG = (msg, classTXT) => {
        if (msg) {
            swal({
                title: msg,
                text: null,
                icon: (classTXT) ? classTXT : 'warning',
                buttons: {
                    ok: 'OK',
                }
            })
        }
    },
    mixedMSG = (status, msg, classTXT) => {
        if (!msg) return;

        if (!status) {
            swal({
                title: msg,
                text: null,
                icon: (classTXT) ? classTXT : 'warning',
                buttons: {
                    ok: 'OK',
                }
            })
        } else {
            toastMSG(msg, classTXT)
        }
        
    },
    swalResponse = (response) => {
        if (!_.isObjectLike(response))
            throw 'Error de conectividad';
        mixedMSG(response.status, response.msg, response.class);
    },
    extractForm = (formArchetype) => {
        let ret = {},
            formArchetypeArr;
        if (_.isFunction(formArchetype))
            formArchetypeArr = formArchetype()
        else
            formArchetypeArr = _.cloneDeep(formArchetype)
        _.forEach(formArchetypeArr, (field) => {
            if (field != null && _.isArray(field)) {
                _.forEach(field, (subField) => {
                    ret[subField.key] = ('default' in subField)
                        ? subField.default
                        : null;
                });
            } else if (field != null && !!field.key) {
                ret[field.key] = ('default' in field) ? field.default : null;
            }
        });
        return ret;
    },
    generarNombreClaseCSS = (cadena) => {
        let nombreClase = cadena;
        // Elimina los espacios en blanco al principio y al final
        nombreClase = nombreClase.trim();
        // Reemplaza los espacios en blanco por guiones
        nombreClase = nombreClase.replace(/\\s+/g, '-');
        // Elimina los caracteres no válidos
        nombreClase = nombreClase.replace(/[^a-zA-Z0-9\\-]/g, '');
        // Asegura que el primer carácter no sea numérico
        nombreClase = nombreClase.replace(/^[0-9]/, '');
        return nombreClase;
    },
    keyMatrix = {
        'en': '_en',
        'es': '_es',
    },

    localStorageGet = (key) => {
        try {
            return window.localStorage.getItem(key);
        } catch (e) {
            console.error(e);
            return null;
        }
    },

    localStorageSet = (key, value) => {
        try {
            window.localStorage.setItem(key, value);
        } catch (e) {
            console.error(e);
        }
    },

    langUserSet = (lang) => {
        try {
            window.localStorage.setItem('language', lang);
            console.log('Language set to:', lang, langUserGet());
        } catch (e) {
            console.error(e);
        }
    },

    langUserGet = () => {
        try {
            const userLang = window.localStorage.getItem('language') || 'es';

            if (userLang !== 'es' && userLang !== 'en') {
                return 'es';
            }

            return userLang.toLowerCase();
        } catch (e) {
            console.error(e);
            return 'es';
        }
    },
    translate = (valueOrigin, label) => {
        try {
            const langConfig = langUserGet();
            if (typeof valueOrigin === 'string') {
                // Corrige que se esté trabajando con un string vacío... :S
                if (!valueOrigin.trim()) return '';
                let trans;
                if (langConfig === 'es') {
                    trans = transEs;
                } else if (langConfig === 'fr') {
                    trans = transFr;
                } else {
                    trans = transEn;
                }
                const normalizedValue = valueOrigin.trim().toLowerCase();
                const transKey = Object.keys(trans).find(key => key.trim().toLowerCase() === normalizedValue);
                if (!transKey && Vue.config.productionTip === false) {
                    console.warn(`Translation missing: "${valueOrigin}" in language: "${langConfig}"`);
                }
                return (transKey ? trans[transKey] : undefined) || valueOrigin;
            } else if (typeof valueOrigin === 'object' && valueOrigin != null) {
                // LABEL VALIDATION
                if (typeof label === 'undefined') throw 'label not recieved!';
                const containingProp = label + keyMatrix[langConfig];
                if ((containingProp) in valueOrigin) {
                    return valueOrigin[containingProp.toLowerCase()];
                } else {
                    return label
                }
            }
        } catch (e) {
            console.error
            return ''
        }
    },
    toTitleCase = (str) => {
        return str
            .toLowerCase()
            .replace(/\b\w/g, match => match.toUpperCase());
    },
    objectToUrlString = (obj) => {
        // console.log('objectToUrlString.obj', obj, _.pickBy(_.cloneDeep(obj), value => !_.isObject(value) && !_.isArray(value)))
        let cObj = _.pickBy(_.cloneDeep(obj), value => !_.isObject(value) && !_.isArray(value)),
            str = Object.entries(cObj)
                .map(([key, value]) => {
                    if (value === true)
                        value = '1';
                    else if (value === false)
                        value = '0';
                    else if (value === null || value === undefined || value === 'undefined')
                        value = null;
                    else
                        value += '';
                    const valueFixed = (_.isString(value)) ? value.replace(/[ ]/g, '_') : value;
                    return (valueFixed) ? `${encodeURIComponent(key)}=${encodeURIComponent(valueFixed)}` : null;
                })
                .join(':');
        return str;
    },
    downloadFile = async (fileName, urlLocation) => {
        try {
            const url = window.URL.createObjectURL(new Blob([await axios.get(urlLocation, {
                responseType: 'blob',
            })]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    },
    getDecimalFromFraction = (fraction) => {
        try {
            let testExp = /(\d+[ ])?\d+\/\d+/,
                decimal = 0.0;
    
            if(testExp.test(fraction)){
                let tieneEnterosExp = /(\d+[ ])\d+\/\d+/;
                
                let fractionTrimmed = fraction.trim().replace(/[ ]+/g, ' ').replace(/\/+/g, '/');
    
                if(!tieneEnterosExp.test(fractionTrimmed))
                    fractionTrimmed = '0 ' + fractionTrimmed;
                
                let numeros = fractionTrimmed.split(' '),
                    parteEntera = parseInt(numeros[0]),
                    fractionSplitted = numeros[1].split('/'),
                    numerador = parseInt(fractionSplitted[0]),
                    denominador = parseInt(fractionSplitted[1]);
    
                return parteEntera + numerador / denominador;
            } else {
                decimal = parseFloat(fraction);
                return decimal;
            }
        } catch(e){
            console.warn('getDecimalFromFraction:' + fraction + ' Not computable!');
            return NaN;
        }
    },
    imageIdToURL=  (value, attrs = {}) => {
        const width = attrs.w || 400,
            height = attrs.h || 300;

        let url;

        if (!value)
            url = `https://placehold.co/${width}x${height}/b3cdbf/ffffff?text=No+Image`;

        if (attrs.w && attrs.h)
            url = `${apiURIMain}/img/render/${value}/${width}/${height}/rezied.jpg`;

        else
            url = `${apiURIMain}/img/render/${value}`;

        console.log('imageIdToURL.url', url);

        return url;
    },
    formatMSISDN =  (value) =>  {
        if (!value || value.length !== 10) return value;
        return `(${value.substring(0, 2)}) ${value.substring(2, 6)}-${value.substring(6, 10)}`;
    },
    assetsImg = (imgName) => {
        return `${$router.options.base}assets/img/${imgName}`;
    }

export {
    notify,
    toastMSG,
    swalMSG,
    confirmFirst,
    mixedMSG,
    arrayToObject,
    extractForm,
    swalResponse,
    generarNombreClaseCSS,
    langUserSet,
    langUserGet,
    translate,
    toTitleCase,
    toHumanReadable,
    objectToUrlString,
    downloadFile,
    singular,
    getDecimalFromFraction,
    Toast,
    imageIdToURL,
    formatMSISDN,
    localStorageGet,
    localStorageSet,
    assetsImg
}